// src/pages/GpmTickets.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useCommands } from '../api/commands';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const STATUSES   = ['Open', 'In Progress', 'Blocked', 'Resolved', 'Closed'];

function fmtDate(d){
  if (!d) return '—';
  const dt = new Date(d);
  return isFinite(dt) ? dt.toLocaleDateString('en-GB') : '—';
}

const STATUS_BADGE = {
  'open': 'badge info',
  'in progress': 'badge warn',
  'blocked': 'badge err',
  'resolved': 'badge ok',
  'closed': 'badge',
};
const PRIORITY_BADGE = {
  'urgent': 'badge err',
  'high': 'badge warn',
};
const statusClass = (s) => STATUS_BADGE[String(s || '').toLowerCase()] || 'badge';
const priorityClass = (p) => PRIORITY_BADGE[String(p || '').toLowerCase()] || 'badge';

/* ---------------- Ticket Editor ---------------- */
function TicketEditor({ ticket, onCancel, onSaved }) {
  const cmd = useCommands();
  const id = ticket._id || ticket.id;

  const [form, setForm] = useState({
    subject:     ticket.subject     || '',
    description: ticket.description || '',
    status:      ticket.status      || 'Open',
    priority:    ticket.priority    || 'Medium',
    reporter:    ticket.reporter    || '',
    contractor:  ticket.contractor  || '',
    office:      ticket.office      || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const canSave = !!form.subject.trim();

  const onChange = (k, v) => setForm(s => ({ ...s, [k]: v }));

  async function persist(patch){
    setSaving(true); setErr('');
    try {
      const withISO = { ...patch };
      if (withISO.resolvedDate instanceof Date) {
        withISO.resolvedDate = withISO.resolvedDate.toISOString();
      }
      const res = await cmd.tickets.update(id, withISO);
      const updated = res?.ticket ? res.ticket : { ...(ticket || {}), ...withISO };
      onSaved(updated);
    } catch(e){
      setErr(e?.response?.data?.error || e?.message || 'Failed to save ticket');
    } finally {
      setSaving(false);
    }
  }

  async function onSave(e){
    e.preventDefault();
    if (!canSave || saving) return;
    await persist({ ...form });
  }

  async function onResolve(){
    if ((form.status || '').toLowerCase() === 'resolved') return;
    await persist({ ...form, status: 'Resolved', resolvedDate: new Date() });
  }

  useEffect(() => {
    function onKey(e){
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's'){
        e.preventDefault();
        if (canSave && !saving) onSave(e);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canSave, saving]); // eslint-disable-line

  return (
    <form onSubmit={onSave}>
      {err && <div className="alert error mb-16">{err}</div>}

      <div className="ticket-editor">
        <div className="stack" style={{ gap: 12 }}>
          <div className="field">
            <label className="input-label">Subject <span className="req">*</span></label>
            <input
              className="input"
              value={form.subject}
              onChange={e=>onChange('subject', e.target.value)}
              placeholder="Short summary"
              autoFocus
            />
          </div>

          <div className="field">
            <label className="input-label">Description</label>
            <textarea
              className="input"
              value={form.description}
              onChange={e=>onChange('description', e.target.value)}
              placeholder="Details, steps, links"
            />
          </div>
        </div>

        <div className="side">
          <div className="form-grid cols-2" style={{ gap: 12 }}>
            <div className="field">
              <label className="input-label">Status</label>
              <select className="input" value={form.status} onChange={e=>onChange('status', e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="field">
              <label className="input-label">Priority</label>
              <select className="input" value={form.priority} onChange={e=>onChange('priority', e.target.value)}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="field">
            <label className="input-label">Office or site</label>
            <input className="input" value={form.office} onChange={e=>onChange('office', e.target.value)} placeholder="Head, Guildford, Northern" />
          </div>

          <div className="field">
            <label className="input-label">Reporter</label>
            <input className="input" value={form.reporter} onChange={e=>onChange('reporter', e.target.value)} placeholder="Person or team" />
          </div>

          <div className="field">
            <label className="input-label">Contractor</label>
            <input className="input" value={form.contractor} onChange={e=>onChange('contractor', e.target.value)} placeholder="Technician or team" />
          </div>

          <div className="form-grid cols-2" style={{ gap: 12 }}>
            <div className="field">
              <span className="input-label">Created</span>
              <div className="readonly-value">{fmtDate(ticket.dateCreated || ticket.timestamp)}</div>
            </div>

            <div className="field">
              <span className="input-label">Resolved</span>
              <div className="readonly-value">{fmtDate(ticket.resolvedDate)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-foot">
        <button className="btn secondary" type="button" onClick={onCancel}>Close</button>
        <button className="btn secondary" type="button" onClick={onResolve} disabled={saving || (form.status||'').toLowerCase()==='resolved'}>Mark resolved</button>
        <button className="btn" type="submit" disabled={!canSave || saving}>{saving ? 'Saving' : 'Save'}</button>
      </div>
    </form>
  );
}


/* ---------------- Page ---------------- */
export default function GpmTickets(){
  const cmd = useCommands();

  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]     = useState('');

  async function load(){
    setLoading(true); setErr('');
    try{
      const list = await cmd.tickets.list();
      setRows(Array.isArray(list) ? list : []);
    }catch(e){
      setErr(e?.response?.data?.error || e?.message || 'Failed to load tickets');
      setRows([]);
    }finally{ setLoading(false); }
  }
  useEffect(()=>{ load(); }, []);

  // filters (compact + inline)
  const [q, setQ] = useState('');
  const [fStatus, setFStatus] = useState('all');
  const [fPriority, setFPriority] = useState('all');
  const [fOffice, setFOffice] = useState('all');

  const offices = useMemo(() => {
    const s = new Set(); rows.forEach(r => r.office && s.add(String(r.office)));
    return ['all', ...Array.from(s).sort((a,b)=>a.localeCompare(b))];
  }, [rows]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter(r => {
      const s  = String(r.status   || '').toLowerCase();
      const pr = String(r.priority || '').toLowerCase();
      const of = String(r.office   || '').toLowerCase();

      if (fStatus   !== 'all' && s  !== fStatus)   return false;
      if (fPriority !== 'all' && pr !== fPriority) return false;
      if (fOffice   !== 'all' && of !== fOffice)   return false;

      if (!t) return true;
      const hay = [
        r.ticketNumber, r.subject, r.description, r.status, r.priority,
        r.reporter, r.contractor, r.office
      ].map(x => String(x || '').toLowerCase());
      return hay.some(sx => sx.includes(t));
    });
  }, [rows, q, fStatus, fPriority, fOffice]);

  // optimistic update helper
  async function quickUpdate(t, patch){
    const id = t._id || t.id; if (!id) return;
    const before = { ...t };
    const isoPatch = { ...patch };
    if (isoPatch.resolvedDate instanceof Date) {
      isoPatch.resolvedDate = isoPatch.resolvedDate.toISOString();
    }
    setRows(rs => rs.map(x => (x._id===id || x.id===id) ? { ...x, ...isoPatch } : x));
    try { await cmd.tickets.update(id, isoPatch); }
    catch(e){
      setRows(rs => rs.map(x => (x._id===id || x.id===id) ? before : x));
      setErr(e?.response?.data?.error || e?.message || 'Failed to update ticket');
    }
  }
  const resolveTicket = (t) => quickUpdate(t, { status:'Resolved', resolvedDate:new Date() });

  // create modal
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    subject:'', description:'', status:'Open', priority:'Medium', reporter:'', contractor:'', office:''
  });
  const canCreate = !!form.subject.trim();
  const onChange = (k,v)=>setForm(s=>({...s,[k]:v}));

  async function submitNew(e){
    e.preventDefault(); if (!canCreate || saving) return;
    setErr(''); setSaving(true);
    try{
      await cmd.tickets.create({
        subject:form.subject.trim(),
        description:form.description.trim(),
        status:form.status, priority:form.priority,
        reporter:form.reporter.trim(), contractor:form.contractor.trim(),
        office:form.office.trim(),
        timestamp:new Date().toISOString(),
      });
      setCreating(false);
      setForm({ subject:'', description:'', status:'Open', priority:'Medium', reporter:'', contractor:'', office:'' });
      await load();
    }catch(e){ setErr(e?.response?.data?.error || e?.message || 'Failed to create ticket'); }
    finally{ setSaving(false); }
  }

  // details modal (opens on row click)
  const [detail, setDetail] = useState(null);

  return (
    <div className="container">
      <PageHeader
        eyebrow="Assets"
        title="Tickets"
        actions={
          <>
            <button className="btn secondary" onClick={load}>Reload</button>
            <button className="btn" onClick={()=>setCreating(true)}><Icon name="plus" size={15} /> New ticket</button>
          </>
        }
      />

      {err && <div className="page-alerts"><div className="alert error">{err}</div></div>}

      <div className="card">
        {/* compact, inline filters */}
        <div className="toolbar mb-16">
          <input className="input grow" type="text" placeholder="Search tickets" value={q} onChange={e=>setQ(e.target.value)} />
          <select className="input" value={fStatus} onChange={e=>setFStatus(e.target.value)}>
            <option value="all">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s.toLowerCase()}>{s}</option>)}
          </select>
          <select className="input" value={fPriority} onChange={e=>setFPriority(e.target.value)}>
            <option value="all">All priorities</option>
            {PRIORITIES.map(p => <option key={p} value={p.toLowerCase()}>{p}</option>)}
          </select>
          <select className="input" value={fOffice} onChange={e=>setFOffice(e.target.value)}>
            {offices.map(o => <option key={o} value={o}>{o==='all'?'All offices':o}</option>)}
          </select>
          {(q || fStatus !== 'all' || fPriority !== 'all' || fOffice !== 'all') && (
            <button
              className="btn ghost"
              onClick={()=>{ setQ(''); setFStatus('all'); setFPriority('all'); setFOffice('all'); }}
            >Clear filters</button>
          )}
        </div>

        {loading ? (
          <div className="empty">Loading</div>
        ) : filtered.length === 0 ? (
          <div className="empty">No tickets found.</div>
        ) : (
          <div className="table-wrap">
            <table className="table tickets-table">
              <colgroup>
                <col className="col-num" />
                <col className="col-subject" />
                <col className="col-status" />
                <col className="col-priority" />
                <col className="col-office" />
                <col className="col-reporter" />
                <col className="col-contract" />
                <col className="col-created" />
                <col className="col-resolved" />
              </colgroup>
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Office</th>
                  <th className="col-reporter">Reporter</th>
                  <th className="col-contract">Contractor</th>
                  <th>Created</th>
                  <th className="col-resolved">Resolved</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => {
                  const id = t._id || t.id;
                  return (
                    <tr key={id} className="clickable" onClick={()=>setDetail(t)}>
                      <td className="cell-strong" title={t.ticketNumber || ''}>{t.ticketNumber || '—'}</td>
                      <td title={t.subject || ''}>{t.subject || '—'}</td>
                      <td title={t.status || ''}><span className={statusClass(t.status)}>{t.status || '—'}</span></td>
                      <td title={t.priority || ''}><span className={priorityClass(t.priority)}>{t.priority || '—'}</span></td>
                      <td title={t.office || ''}>{t.office || '—'}</td>
                      <td className="col-reporter" title={t.reporter || ''}>{t.reporter || '—'}</td>
                      <td className="col-contract" title={t.contractor || ''}>{t.contractor || '—'}</td>
                      <td className="nowrap">{fmtDate(t.dateCreated || t.timestamp)}</td>
                      <td className="col-resolved nowrap">{fmtDate(t.resolvedDate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create modal */}
      {creating && (
        <div className="modal-backdrop" onClick={e=>{ if (e.target===e.currentTarget) setCreating(false); }}>
          <div className="modal lg" role="dialog" aria-modal="true">
            <div className="modal-head">
              <h3>New ticket</h3>
            </div>
            <form onSubmit={submitNew}>
              <div className="form-grid">
                <label className="input-label span-all">Subject <span className="req">*</span>
                  <input className="input" value={form.subject} onChange={e=>onChange('subject', e.target.value)} placeholder="Short summary" autoFocus />
                </label>
                <label className="input-label span-all">Description
                  <textarea className="input" rows={4} value={form.description} onChange={e=>onChange('description', e.target.value)} placeholder="Details" />
                </label>
                <label className="input-label">Status
                  <select className="input" value={form.status} onChange={e=>onChange('status', e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="input-label">Priority
                  <select className="input" value={form.priority} onChange={e=>onChange('priority', e.target.value)}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
                <label className="input-label">Office or site
                  <input className="input" value={form.office} onChange={e=>onChange('office', e.target.value)} placeholder="Head, Guildford, Northern" />
                </label>
                <label className="input-label">Reporter
                  <input className="input" value={form.reporter} onChange={e=>onChange('reporter', e.target.value)} placeholder="Person or team" />
                </label>
                <label className="input-label">Contractor
                  <input className="input" value={form.contractor} onChange={e=>onChange('contractor', e.target.value)} placeholder="Technician or team" />
                </label>
              </div>
              <div className="modal-foot">
                <button className="btn secondary" type="button" onClick={()=>setCreating(false)}>Cancel</button>
                <button className="btn" type="submit" disabled={!canCreate || saving}>{saving ? 'Creating' : 'Create ticket'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details modal */}
      {detail && (
        <div className="modal-backdrop" onClick={e=>{ if (e.target===e.currentTarget) setDetail(null); }}>
          <div className="modal xl" role="dialog" aria-modal="true">
            <div className="modal-head">
              <h3>{detail.ticketNumber || 'Ticket'}</h3>
            </div>
            <TicketEditor
              ticket={detail}
              onCancel={()=>setDetail(null)}
              onSaved={(updated)=> {
                const id = updated._id || updated.id;
                setRows(rs => rs.map(x => ((x._id||x.id)===id) ? updated : x));
                setDetail(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
